import { useEffect, useRef } from 'react';

export interface ShortcutDef {
  /** Key string — e.g. 'j', 'k', 'e', 'Enter', '?', 'Escape' */
  key: string;
  /** Requires Ctrl (or Cmd on Mac) */
  ctrl?: boolean;
  /** Requires Shift */
  shift?: boolean;
  /** Fire even when an input/textarea/select is focused */
  ignoreGuard?: boolean;
  handler: () => void;
}

function isInputFocused(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName.toLowerCase();
  return (
    tag === 'input' ||
    tag === 'textarea' ||
    tag === 'select' ||
    el.getAttribute('contenteditable') === 'true'
  );
}

/**
 * Registers keyboard shortcuts for the lifetime of the component.
 * Uses a ref so callers can pass inline arrays without causing re-registration.
 */
export function useKeyboardShortcuts(shortcuts: ShortcutDef[]) {
  const shortcutsRef = useRef(shortcuts);
  shortcutsRef.current = shortcuts;

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      for (const s of shortcutsRef.current) {
        const pressedKey = typeof e.key === 'string' ? e.key : '';
        const shortcutKey = typeof s.key === 'string' ? s.key : '';
        const keyMatch =
          pressedKey === shortcutKey || pressedKey.toLowerCase() === shortcutKey.toLowerCase();
        if (!keyMatch) continue;

        const needsCtrl = s.ctrl ?? false;
        const needsShift = s.shift ?? false;

        // Ctrl/Cmd check: accept either Ctrl or Meta for cross-platform
        const ctrlOk = needsCtrl ? e.ctrlKey || e.metaKey : !e.ctrlKey && !e.metaKey;
        if (!ctrlOk) continue;

        // Shift check
        if (e.shiftKey !== needsShift) continue;

        // Guard: don't fire plain-key shortcuts while typing
        if (!s.ignoreGuard && isInputFocused()) continue;

        e.preventDefault();
        s.handler();
        break;
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
}
